import { Box, Container, Typography, Divider } from '@mui/joy';
import type { ReactElement } from 'react';
import GLink from 'components/technical/GLink/GLink';
import BulletItem from './BulletItem';

const PrivacyPolicy = (): ReactElement => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box>
        <Typography level="h1" sx={{ mb: 2 }}>
          Privacy Policy
        </Typography>

        <Typography level="body-sm" sx={{ mb: 4, color: 'text.secondary' }}>
          Last updated August 05, 2023
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography level="body-md">
              This Privacy Notice for Genie AI (Kindly Inc.) ("we," "us," or "our"), describes how and why we might
              access, collect, store, use, and/or share ("process") your personal information when you use our services
              ("Services"), including when you:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>
                Visit our website at https://app.genieai.tech, or any website of ours that links to this Privacy Notice
              </BulletItem>
              <BulletItem>Engage with us in other related ways, including any sales, marketing, or events</BulletItem>
            </Box>
            <Typography level="body-md">
              <strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your privacy
              rights and choices. We are responsible for making decisions about how your personal information is
              processed. If you do not agree with our policies and practices, please do not use our Services. If you
              still have any questions or concerns, please contact us at legal@genieai.tech.
            </Typography>
          </Box>

          <Box>
            <Typography level="h2" sx={{ mb: 2 }}>
              Summary of Key Points
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <em>
                This summary provides key points from our Privacy Notice, but you can find out more details about any of
                these topics by clicking the link following each key point or by using our table of contents below to
                find the section you are looking for.
              </em>
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>What personal information do we process?</strong> When you visit, use, or navigate our Services,
              we may process personal information depending on how you interact with us and the Services, the choices
              you make, and the products and features you use.{' '}
              <GLink to="#section-1">Learn more about personal information you disclose to us.</GLink>
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>Do we process any sensitive personal information?</strong> Some of the information may be
              considered "special" or "sensitive" in certain jurisdictions, for example your racial or ethnic origins,
              sexual orientation, and religious beliefs. We may process sensitive personal information when necessary
              with your consent or as otherwise permitted by applicable law.{' '}
              <GLink to="#section-1">Learn more about sensitive information we process.</GLink>
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>Do we collect any information from third parties?</strong> We do not collect any information from
              third parties.
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>How do we process your information?</strong> We process your information to provide, improve, and
              administer our Services, communicate with you, for security and fraud prevention, and to comply with law.
              We may also process your information for other purposes with your consent. We process your information
              only when we have a valid legal reason to do so.{' '}
              <GLink to="#section-2">Learn more about how we process your information.</GLink>
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>In what situations and with which parties do we share personal information?</strong> We may share
              information in specific situations and with specific third parties.{' '}
              <GLink to="#section-4">Learn more about when and with whom we share your personal information.</GLink>
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>How do we keep your information safe?</strong> We have adequate organizational and technical
              processes and procedures in place to protect your personal information. However, no electronic
              transmission over the internet or information storage technology can be guaranteed to be 100% secure, so
              we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not
              be able to defeat our security and improperly collect, access, steal, or modify your information.{' '}
              <GLink to="#section-8">Learn more about how we keep your information safe.</GLink>
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>What are your rights?</strong> Depending on where you are located geographically, the applicable
              privacy law may mean you have certain rights regarding your personal information.{' '}
              <GLink to="#section-10">Learn more about your privacy rights.</GLink>
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>How do you exercise your rights?</strong> The easiest way to exercise your rights is by submitting
              a data subject access request, or by contacting us. We will consider and act upon any request in
              accordance with applicable data protection laws.
            </Typography>

            <Typography level="body-md">
              Want to learn more about what we do with any information we collect? Review the Privacy Notice in full.
            </Typography>
          </Box>

          <Box>
            <Typography level="h2" sx={{ mb: 2 }}>
              Table of Contents
            </Typography>
            <Box component="ol" sx={{ pl: 3 }}>
              <BulletItem>
                <GLink to="#section-1">WHAT INFORMATION DO WE COLLECT?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-2">HOW DO WE PROCESS YOUR INFORMATION?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-3">WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-4">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-5">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-6">HOW DO WE HANDLE YOUR SOCIAL LOGINS?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-7">HOW LONG DO WE KEEP YOUR INFORMATION?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-8">HOW DO WE KEEP YOUR INFORMATION SAFE?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-9">DO WE COLLECT INFORMATION FROM MINORS?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-10">WHAT ARE YOUR PRIVACY RIGHTS?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-11">CONTROLS FOR DO-NOT-TRACK FEATURES</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-12">DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-13">DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-14">DO WE MAKE UPDATES TO THIS NOTICE?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-15">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</GLink>
              </BulletItem>
              <BulletItem>
                <GLink to="#section-16">HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</GLink>
              </BulletItem>
            </Box>
          </Box>

          <Box id="section-1">
            <Typography level="h2" sx={{ mb: 2 }}>
              1. What Information Do We Collect?
            </Typography>

            <Typography level="h3" sx={{ mb: 1 }}>
              Personal information you disclose to us
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>We collect personal information that you provide to us.</em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We collect personal information that you voluntarily provide to us when you register on the Services,
              express an interest in obtaining information about us or our products and Services, when you participate
              in activities on the Services, or otherwise when you contact us.
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>Personal Information Provided by You.</strong> The personal information that we collect depends on
              the context of your interactions with us and the Services, the choices you make, and the products and
              features you use. The personal information we collect may include the following:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>names</BulletItem>
              <BulletItem>email addresses</BulletItem>
              <BulletItem>usernames</BulletItem>
              <BulletItem>passwords</BulletItem>
              <BulletItem>job titles</BulletItem>
            </Box>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>Sensitive Information.</strong> When necessary, with your consent or as otherwise permitted by
              applicable law, we process the following categories of sensitive information:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>financial data</BulletItem>
            </Box>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>Social Media Login Data.</strong> We may provide you with the option to register with us using
              your existing social media account details, like your Facebook, X, or other social media account. If you
              choose to register in this way, we will collect certain profile information about you from the social
              media provider, as described in the section called "HOW DO WE HANDLE YOUR SOCIAL LOGINS?" below.
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              All personal information that you provide to us must be true, complete, and accurate, and you must notify
              us of any changes to such personal information.
            </Typography>

            <Typography level="h3" sx={{ mb: 1, mt: 3 }}>
              Information automatically collected
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>
                Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics
                — is collected automatically when you visit our Services.
              </em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We automatically collect certain information when you visit, use, or navigate the Services. This
              information does not reveal your specific identity (like your name or contact information) but may include
              device and usage information, such as your IP address, browser and device characteristics, operating
              system, language preferences, referring URLs, device name, country, location, information about how and
              when you use our Services, and other technical information. This information is primarily needed to
              maintain the security and operation of our Services, and for our internal analytics and reporting
              purposes.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Like many businesses, we also collect information through cookies and similar technologies. You can find
              out more about this in our Cookie Notice: https://genieai.tech/cookies.
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              The information we collect includes:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>
                <strong>Log and Usage Data.</strong> Log and usage data is service-related, diagnostic, usage, and
                performance information our servers automatically collect when you access or use our Services and which
                we record in log files. Depending on how you interact with us, this log data may include your IP
                address, device information, browser type, and settings and information about your activity in the
                Services (such as the date/time stamps associated with your usage, pages and files viewed, searches, and
                other actions you take such as which features you use), device event information (such as system
                activity, error reports (sometimes called "crash dumps"), and hardware settings).
              </BulletItem>
              <BulletItem>
                <strong>Location Data.</strong> We collect location data such as information about your device's
                location, which can be either precise or imprecise. How much information we collect depends on the type
                and settings of the device you use to access the Services. For example, we may use GPS and other
                technologies to collect geolocation data that tells us your current location (based on your IP address).
                You can opt out of allowing us to collect this information either by refusing access to the information
                or by disabling your Location setting on your device. However, if you choose to opt out, you may not be
                able to use certain aspects of the Services.
              </BulletItem>
            </Box>
          </Box>

          <Box id="section-2">
            <Typography level="h2" sx={{ mb: 2 }}>
              2. How Do We Process Your Information?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>
                We process your information to provide, improve, and administer our Services, communicate with you, for
                security and fraud prevention, and to comply with law. We may also process your information for other
                purposes with your consent.
              </em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We process your personal information for a variety of reasons, depending on how you interact with our
              Services, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <BulletItem>
                To facilitate account creation and authentication and otherwise manage user accounts. We may process
                your information so you can create and log in to your account, as well as keep your account in working
                order.
              </BulletItem>
              <BulletItem>
                To deliver and facilitate delivery of services to the user. We may process your information to provide
                you with the requested service.
              </BulletItem>
              <BulletItem>
                To respond to user inquiries/offer support to users. We may process your information to respond to your
                inquiries and solve any potential issues you might have with the requested service.
              </BulletItem>
              <BulletItem>
                To send administrative information to you. We may process your information to send you details about our
                products and services, changes to our terms and policies, and other similar information.
              </BulletItem>
              <BulletItem>
                To fulfill and manage your orders. We may process your information to fulfill and manage your orders,
                payments, returns, and exchanges made through the Services.
              </BulletItem>
              <BulletItem>
                To request feedback. We may process your information when necessary to request feedback and to contact
                you about your use of our Services.
              </BulletItem>
              <BulletItem>
                To protect our Services. We may process your information as part of our efforts to keep our Services
                safe and secure, including fraud monitoring and prevention.
              </BulletItem>
              <BulletItem>
                To identify usage trends. We may process information about how you use our Services to better understand
                how they are being used so we can improve them.
              </BulletItem>
              <BulletItem>
                To save or protect an individual's vital interest. We may process your information when necessary to
                save or protect an individual's vital interest, such as to prevent harm.
              </BulletItem>
              <BulletItem>
                To provide customized insights into user data. User data might be used as part of the development of
                machine learning, artificial intelligence, and quantitative models aimed at generating user insights.
              </BulletItem>
            </Box>
          </Box>

          <Box id="section-3">
            <Typography level="h2" sx={{ mb: 2 }}>
              3. What Legal Bases Do We Rely On to Process Your Information?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>
                We only process your personal information when we believe it is necessary and we have a valid legal
                reason (i.e., legal basis) to do so under applicable law, like with your consent, to comply with laws,
                to provide you with services to enter into or fulfill our contractual obligations, to protect your
                rights, or to fulfill our legitimate business interests.
              </em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>If you are located in the EU or UK, this section applies to you.</strong>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid legal bases we
              rely on in order to process your personal information. As such, we may rely on the following legal bases
              to process your personal information:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>
                <strong>Consent.</strong> We may process your information if you have given us permission (i.e.,
                consent) to use your personal information for a specific purpose. You can withdraw your consent at any
                time. Learn more about withdrawing your consent.
              </BulletItem>
              <BulletItem>
                <strong>Performance of a Contract.</strong> We may process your personal information when we believe it
                is necessary to fulfill our contractual obligations to you, including providing our Services or at your
                request prior to entering into a contract with you.
              </BulletItem>
              <BulletItem>
                <strong>Legitimate Interests.</strong> We may process your information when we believe it is reasonably
                necessary to achieve our legitimate business interests and those interests do not outweigh your
                interests and fundamental rights and freedoms. For example, we may process your personal information for
                some of the purposes described in order to:
                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                  <BulletItem>
                    Analyze how our Services are used so we can improve them to engage and retain users
                  </BulletItem>
                  <BulletItem>Diagnose problems and/or prevent fraudulent activities</BulletItem>
                  <BulletItem>
                    Understand how our users use our products and services so we can improve user experience
                  </BulletItem>
                </Box>
              </BulletItem>
              <BulletItem>
                <strong>Legal Obligations.</strong> We may process your information where we believe it is necessary for
                compliance with our legal obligations, such as to cooperate with a law enforcement body or regulatory
                agency, exercise or defend our legal rights, or disclose your information as evidence in litigation in
                which we are involved.
              </BulletItem>
              <BulletItem>
                <strong>Vital Interests.</strong> We may process your information where we believe it is necessary to
                protect your vital interests or the vital interests of a third party, such as situations involving
                potential threats to the safety of any person.
              </BulletItem>
            </Box>

            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>If you are located in Canada, this section applies to you.</strong>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We may process your information if you have given us specific permission (i.e., express consent) to use
              your personal information for a specific purpose, or in situations where your permission can be inferred
              (i.e., implied consent). You can withdraw your consent at any time.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              In some exceptional cases, we may be legally permitted under applicable law to process your information
              without your consent, including, for example:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <BulletItem>
                If collection is clearly in the interests of an individual and consent cannot be obtained in a timely
                way
              </BulletItem>
              <BulletItem>For investigations and fraud detection and prevention</BulletItem>
              <BulletItem>For business transactions provided certain conditions are met</BulletItem>
              <BulletItem>
                If it is contained in a witness statement and the collection is necessary to assess, process, or settle
                an insurance claim
              </BulletItem>
              <BulletItem>
                For identifying injured, ill, or deceased persons and communicating with next of kin
              </BulletItem>
              <BulletItem>
                If we have reasonable grounds to believe an individual has been, is, or may be victim of financial abuse
              </BulletItem>
              <BulletItem>
                If it is reasonable to expect collection and use with consent would compromise the availability or the
                accuracy of the information and the collection is reasonable for purposes related to investigating a
                breach of an agreement or a contravention of the laws of Canada or a province
              </BulletItem>
              <BulletItem>
                If disclosure is required to comply with a subpoena, warrant, court order, or rules of the court
                relating to the production of records
              </BulletItem>
              <BulletItem>
                If it was produced by an individual in the course of their employment, business, or profession and the
                collection is consistent with the purposes for which the information was produced
              </BulletItem>
              <BulletItem>If the collection is solely for journalistic, artistic, or literary purposes</BulletItem>
              <BulletItem>If the information is publicly available and is specified by the regulations</BulletItem>
            </Box>
          </Box>

          <Box id="section-4">
            <Typography level="h2" sx={{ mb: 2 }}>
              4. When and With Whom Do We Share Your Personal Information?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>
                We may share information in specific situations described in this section and/or with the following
                third parties.
              </em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We may need to share your personal information in the following situations:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <BulletItem>
                <strong>Business Transfers.</strong> We may share or transfer your information in connection with, or
                during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a
                portion of our business to another company.
              </BulletItem>
              <BulletItem>
                <strong>Affiliates.</strong> We may share your information with our affiliates, in which case we will
                require those affiliates to honor this Privacy Notice. Affiliates include our parent company and any
                subsidiaries, joint venture partners, or other companies that we control or that are under common
                control with us.
              </BulletItem>
              <BulletItem>
                <strong>Business Partners.</strong> We may share your information with our business partners to offer
                you certain products, services, or promotions.
              </BulletItem>
            </Box>
          </Box>

          <Box id="section-5">
            <Typography level="h2" sx={{ mb: 2 }}>
              5. Do We Use Cookies and Other Tracking Technologies?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>We may use cookies and other tracking technologies to collect and store your information.</em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information
              when you interact with our Services. Some online tracking technologies help us maintain the security of
              our Services and your account, prevent crashes, fix bugs, save your preferences, and assist with basic
              site functions.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We also permit third parties and service providers to use online tracking technologies on our Services for
              analytics and advertising, including to help manage and display advertisements, to tailor advertisements
              to your interests, or to send abandoned shopping cart reminders (depending on your communication
              preferences). The third parties and service providers use their technology to provide advertising about
              products and services tailored to your interests which may appear either on our Services or on other
              websites.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              To the extent these online tracking technologies are deemed to be a "sale"/"sharing" (which includes
              targeted advertising, as defined under the applicable laws) under applicable US state laws, you can opt
              out of these online tracking technologies by submitting a request as described below under section "DO
              UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?"
            </Typography>
            <Typography level="body-md">
              Specific information about how we use such technologies and how you can refuse certain cookies is set out
              in our Cookie Notice: https://genieai.tech/cookies.
            </Typography>
          </Box>

          <Box id="section-6">
            <Typography level="h2" sx={{ mb: 2 }}>
              6. How Do We Handle Your Social Logins?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>
                If you choose to register or log in to our Services using a social media account, we may have access to
                certain information about you.
              </em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Our Services offer you the ability to register and log in using your third-party social media account
              details (like your Facebook or X logins). Where you choose to do this, we will receive certain profile
              information about you from your social media provider. The profile information we receive may vary
              depending on the social media provider concerned, but will often include your name, email address, friends
              list, and profile picture, as well as other information you choose to make public on such a social media
              platform.
            </Typography>
            <Typography level="body-md">
              We will use the information we receive only for the purposes that are described in this Privacy Notice or
              that are otherwise made clear to you on the relevant Services. Please note that we do not control, and are
              not responsible for, other uses of your personal information by your third-party social media provider. We
              recommend that you review their privacy notice to understand how they collect, use, and share your
              personal information, and how you can set your privacy preferences on their sites and apps.
            </Typography>
          </Box>

          <Box id="section-7">
            <Typography level="h2" sx={{ mb: 2 }}>
              7. How Long Do We Keep Your Information?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>
                We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy
                Notice unless otherwise required by law.
              </em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We will only keep your personal information for as long as it is necessary for the purposes set out in
              this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax,
              accounting, or other legal requirements). No purpose in this notice will require us keeping your personal
              information for longer than the period of time in which users have an account with us.
            </Typography>
            <Typography level="body-md">
              When we have no ongoing legitimate business need to process your personal information, we will either
              delete or anonymize such information, or, if this is not possible (for example, because your personal
              information has been stored in backup archives), then we will securely store your personal information and
              isolate it from any further processing until deletion is possible.
            </Typography>
          </Box>

          <Box id="section-8">
            <Typography level="h2" sx={{ mb: 2 }}>
              8. How Do We Keep Your Information Safe?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>
                We aim to protect your personal information through a system of organizational and technical security
                measures.
              </em>
            </Typography>
            <Typography level="body-md">
              We have implemented appropriate and reasonable technical and organizational security measures designed to
              protect the security of any personal information we process. However, despite our safeguards and efforts
              to secure your information, no electronic transmission over the Internet or information storage technology
              can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or
              other unauthorized third parties will not be able to defeat our security and improperly collect, access,
              steal, or modify your information. Although we will do our best to protect your personal information,
              transmission of personal information to and from our Services is at your own risk. You should only access
              the Services within a secure environment.
            </Typography>
          </Box>

          <Box id="section-9">
            <Typography level="h2" sx={{ mb: 2 }}>
              9. Do We Collect Information From Minors?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>We do not knowingly collect data from or market to children under 18 years of age.</em>
            </Typography>
            <Typography level="body-md">
              We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we
              knowingly sell such personal information. By using the Services, you represent that you are at least 18 or
              that you are the parent or guardian of such a minor and consent to such minor dependent's use of the
              Services. If we learn that personal information from users less than 18 years of age has been collected,
              we will deactivate the account and take reasonable measures to promptly delete such data from our records.
              If you become aware of any data we may have collected from children under age 18, please contact us at
              federico@genieai.tech.
            </Typography>
          </Box>

          <Box id="section-10">
            <Typography level="h2" sx={{ mb: 2 }}>
              10. What Are Your Privacy Rights?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>
                Depending on your state of residence in the US or in some regions, such as the European Economic Area
                (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that allow you greater access to
                and control over your personal information. You may review, change, or terminate your account at any
                time, depending on your country, province, or state of residence.
              </em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under applicable data
              protection laws. These may include the right (i) to request access and obtain a copy of your personal
              information, (ii) to request rectification or erasure; (iii) to restrict the processing of your personal
              information; (iv) if applicable, to data portability; and (v) not to be subject to automated
              decision-making. In certain circumstances, you may also have the right to object to the processing of your
              personal information. You can make such a request by contacting us by using the contact details provided
              in the section <GLink to="#section-15">"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</GLink> below.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We will consider and act upon any request in accordance with applicable data protection laws.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              If you are located in the EEA or UK and you believe we are unlawfully processing your personal
              information, you also have the right to complain to your Member State data protection authority or UK data
              protection authority.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              If you are located in Switzerland, you may contact the Federal Data Protection and Information
              Commissioner.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>Withdrawing your consent:</strong> If we are relying on your consent to process your personal
              information, which may be express and/or implied consent depending on the applicable law, you have the
              right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by
              using the contact details provided in the section{' '}
              <GLink to="#section-15">"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</GLink> below.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              However, please note that this will not affect the lawfulness of the processing before its withdrawal nor,
              when applicable law allows, will it affect the processing of your personal information conducted in
              reliance on lawful processing grounds other than consent.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>Account Information</strong>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              If you would at any time like to review or change the information in your account or terminate your
              account, you can:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>Contact us using the contact information provided.</BulletItem>
            </Box>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Upon your request to terminate your account, we will deactivate or delete your account and information
              from our active databases. However, we may retain some information in our files to prevent fraud,
              troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with
              applicable legal requirements.
            </Typography>
            <Typography level="body-md">
              <strong>Cookies and similar technologies:</strong> Most Web browsers are set to accept cookies by default.
              If you prefer, you can usually choose to set your browser to remove cookies and to reject cookies. If you
              choose to remove cookies or reject cookies, this could affect certain features or services of our
              Services. For further information, please see our Cookie Notice: https://genieai.tech/cookies.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              If you have questions or comments about your privacy rights, you may email us at legal@genieai.tech.
            </Typography>
          </Box>

          <Box id="section-11">
            <Typography level="h2" sx={{ mb: 2 }}>
              11. Controls for Do-Not-Track Features
            </Typography>
            <Typography level="body-md">
              Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT")
              feature or setting you can activate to signal your privacy preference not to have data about your online
              browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing
              and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser
              signals or any other mechanism that automatically communicates your choice not to be tracked online. If a
              standard for online tracking is adopted that we must follow in the future, we will inform you about that
              practice in a revised version of this Privacy Notice.
            </Typography>
            <Typography level="body-md" sx={{ mt: 2 }}>
              California law requires us to let you know how we respond to web browser DNT signals. Because there
              currently is not an industry or legal standard for recognizing or honoring DNT signals, we do not respond
              to them at this time.
            </Typography>
          </Box>

          <Box id="section-12">
            <Typography level="h2" sx={{ mb: 2 }}>
              12. Do United States Residents Have Specific Privacy Rights?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>
                If you are a resident of California or Virginia, you may have the right to request access to and receive
                details about the personal information we maintain about you and how we have processed it, correct
                inaccuracies, get a copy of, or delete your personal information. You may also have the right to
                withdraw your consent to our processing of your personal information. These rights may be limited in
                some circumstances by applicable law.
              </em>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <em>More information is provided below.</em>
            </Typography>

            <Typography level="h3" sx={{ mb: 2 }}>
              Categories of Personal Information We Collect
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We have collected the following categories of personal information in the past twelve (12) months:
            </Typography>

            <Box sx={{ mb: 3, overflow: 'auto' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr auto',
                  gap: 2,
                  mb: 2,
                  fontWeight: 'bold',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                <Typography level="body-md">
                  <strong>Category</strong>
                </Typography>
                <Typography level="body-md">
                  <strong>Examples</strong>
                </Typography>
                <Typography level="body-md">
                  <strong>Collected</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>A. Identifiers</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique
                  personal identifier, online identifier, Internet Protocol address, email address, and account name
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>YES</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>B. Personal information as defined in the California Customer Records statute</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Name, contact information, education, employment, employment history, and financial information
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>YES</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>C. Protected classification characteristics under state or federal law</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic
                  data
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>NO</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>D. Commercial information</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Transaction information, purchase history, financial details, and payment information
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>YES</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>E. Biometric information</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Fingerprints and voiceprints
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>NO</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>F. Internet or other similar network activity</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Browsing history, search history, online behavior, interest data, and interactions with our and other
                  websites, applications, systems, and advertisements
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>NO</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>G. Geolocation data</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Device location
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>YES</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>H. Audio, electronic, sensory, or similar information</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Images and audio, video or call recordings created in connection with our business activities
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>NO</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>I. Professional or employment-related information</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Business contact details in order to provide you our Services at a business level or job title, work
                  history, and professional qualifications if you apply for a job with us
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>NO</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>J. Education Information</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Student records and directory information
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>NO</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>K. Inferences drawn from collected personal information</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Inferences drawn from any of the collected personal information listed above to create a profile or
                  summary about, for example, an individual's preferences and characteristics
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>NO</strong>
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 2, mb: 2, alignItems: 'start' }}>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>L. Sensitive personal Information</strong>
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  Account login information, contents of email or text messages and precise geolocation
                </Typography>
                <Typography level="body-md" sx={{ mb: 1 }}>
                  <strong>YES</strong>
                </Typography>
              </Box>
            </Box>

            <Typography level="body-md" sx={{ mb: 2 }}>
              We only collect sensitive personal information, as defined by applicable privacy laws or the purposes
              allowed by law or with your consent. Sensitive personal information may be used, or disclosed to a service
              provider or contractor, for additional, specified purposes. You may have the right to limit the use or
              disclosure of your sensitive personal information. We do not collect or process sensitive personal
              information for the purpose of inferring characteristics about you.
            </Typography>

            <Typography level="body-md" sx={{ mb: 2 }}>
              We may also collect other personal information outside of these categories through instances where you
              interact with us in person, online, or by phone or mail in the context of:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>Receiving help through our customer support channels;</BulletItem>
              <BulletItem>Participation in customer surveys or contests; and</BulletItem>
              <BulletItem>Facilitation in the delivery of our Services and to respond to your inquiries.</BulletItem>
            </Box>

            <Typography level="body-md" sx={{ mb: 2 }}>
              We will use and retain the collected personal information as needed to provide the Services or for:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>Category A - As long as the user has an account with us</BulletItem>
              <BulletItem>Category B - As long as the user has an account with us</BulletItem>
              <BulletItem>Category D - As long as the user has an account with us</BulletItem>
              <BulletItem>Category G - As long as the user has an account with us</BulletItem>
              <BulletItem>Category L - As long as the user has an account with us</BulletItem>
            </Box>

            <Typography level="h3" sx={{ mb: 1 }}>
              Sources of Personal Information
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Learn more about the sources of personal information we collect in{' '}
              <GLink to="#section-1">"WHAT INFORMATION DO WE COLLECT?"</GLink>
            </Typography>

            <Typography level="h3" sx={{ mb: 1 }}>
              How We Use and Share Personal Information
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Learn about how we use your personal information in the section,{' '}
              <GLink to="#section-2">"HOW DO WE PROCESS YOUR INFORMATION?"</GLink>
            </Typography>

            <Typography level="h3" sx={{ mb: 1 }}>
              Will your information be shared with anyone else?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We may disclose your personal information with our service providers pursuant to a written contract
              between us and each service provider. Learn more about how we disclose personal information to in the
              section, <GLink to="#section-4">"WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?"</GLink>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We may use your personal information for our own business purposes, such as for undertaking internal
              research for technological development and demonstration. This is not considered to be "selling" of your
              personal information.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We have not disclosed, sold, or shared any personal information to third parties for a business or
              commercial purpose in the preceding twelve (12) months. We will not sell or share personal information in
              the future belonging to website visitors, users, and other consumers.
            </Typography>

            <Typography level="h3" sx={{ mb: 1 }}>
              Your Rights
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              You have rights under certain US state data protection laws. However, these rights are not absolute, and
              in certain cases, we may decline your request as permitted by law. These rights include:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>Right to know whether or not we are processing your personal data</BulletItem>
              <BulletItem>Right to access your personal data</BulletItem>
              <BulletItem>Right to correct inaccuracies in your personal data</BulletItem>
              <BulletItem>Right to request the deletion of your personal data</BulletItem>
              <BulletItem>Right to obtain a copy of the personal data you previously shared with us</BulletItem>
              <BulletItem>Right to non-discrimination for exercising your rights</BulletItem>
              <BulletItem>
                Right to opt out of the processing of your personal data if it is used for targeted advertising (or
                sharing as defined under California's privacy law), the sale of personal data, or profiling in
                furtherance of decisions that produce legal or similarly significant effects ("profiling")
              </BulletItem>
            </Box>

            <Typography level="body-md" sx={{ mb: 2 }}>
              Depending upon the state where you live, you may also have the following rights:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>
                Right to obtain a list of the categories of third parties to which we have disclosed personal data (as
                permitted by applicable law, including California's privacy law)
              </BulletItem>
              <BulletItem>
                Right to limit use and disclosure of sensitive personal data (as permitted by applicable law, including
                California's privacy law)
              </BulletItem>
            </Box>

            <Typography level="h3" sx={{ mb: 1 }}>
              How to Exercise Your Rights
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              To exercise these rights, you can contact us by submitting a data subject access request, by emailing us
              at federico@genieai.tech, or by referring to the contact details at the bottom of this document.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Under certain US state data protection laws, you can designate an authorized agent to make a request on
              your behalf. We may deny a request from an authorized agent that does not submit proof that they have been
              validly authorized to act on your behalf in accordance with applicable laws.
            </Typography>

            <Typography level="h3" sx={{ mb: 1 }}>
              Request Verification
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Upon receiving your request, we will need to verify your identity to determine you are the same person
              about whom we have the information in our system. We will only use personal information provided in your
              request to verify your identity or authority to make the request. However, if we cannot verify your
              identity from the information already maintained by us, we may request that you provide additional
              information for the purposes of verifying your identity and for security or fraud-prevention purposes.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              If you submit the request through an authorized agent, we may need to collect additional information to
              verify your identity before processing your request and the agent will need to provide a written and
              signed permission from you to submit such request on your behalf.
            </Typography>

            <Typography level="h3" sx={{ mb: 1 }}>
              California "Shine The Light" Law
            </Typography>
            <Typography level="body-md">
              California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users who are
              California residents to request and obtain from us, once a year and free of charge, information about
              categories of personal information (if any) we disclosed to third parties for direct marketing purposes
              and the names and addresses of all third parties with which we shared personal information in the
              immediately preceding calendar year. If you are a California resident and would like to make such a
              request, please submit your request in writing to us by using the contact details provided in the section
              <GLink to="#section-15">"HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"</GLink>
            </Typography>
          </Box>

          <Box id="section-13">
            <Typography level="h2" sx={{ mb: 2 }}>
              13. Do Other Regions Have Specific Privacy Rights?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>You may have additional rights based on the country you reside in.</em>
            </Typography>

            <Typography level="h3" sx={{ mb: 1 }}>
              Australia and New Zealand
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              We collect and process your personal information under the obligations and conditions set by Australia's
              Privacy Act 1988 and New Zealand's Privacy Act 2020 (Privacy Act).
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              This Privacy Notice satisfies the notice requirements defined in both Privacy Acts, in particular: what
              personal information we collect from you, from which sources, for which purposes, and other recipients of
              your personal information.
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              If you do not wish to provide the personal information necessary to fulfill their applicable purpose, it
              may affect our ability to provide our services, in particular:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <BulletItem>offer you the products or services that you want</BulletItem>
              <BulletItem>respond to or help with your requests</BulletItem>
              <BulletItem>manage your account with us</BulletItem>
              <BulletItem>confirm your identity and protect your account</BulletItem>
            </Box>
            <Typography level="body-md" sx={{ mb: 2 }}>
              At any time, you have the right to request access to or correction of your personal information. You can
              make such a request by contacting us by using the contact details provided in the section{' '}
              <GLink to="#section-16">"HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?"</GLink>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              If you believe we are unlawfully processing your personal information, you have the right to submit a
              complaint about a breach of the Australian Privacy Principles to the Office of the Australian Information
              Commissioner and a breach of New Zealand's Privacy Principles to the Office of New Zealand Privacy
              Commissioner.
            </Typography>

            <Typography level="h3" sx={{ mb: 1 }}>
              Republic of South Africa
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              At any time, you have the right to request access to or correction of your personal information. You can
              make such a request by contacting us by using the contact details provided in the section{' '}
              <GLink to="#section-16">"HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?"</GLink>
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              If you are unsatisfied with the manner in which we address any complaint with regard to our processing of
              personal information, you can contact the office of the regulator, the details of which are:
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>The Information Regulator (South Africa)</strong>
              <br />
              General enquiries: enquiries@inforegulator.org.za
              <br />
              Complaints (complete POPIA/PAIA form 5): PAIAComplaints@inforegulator.org.za &
              POPIAComplaints@inforegulator.org.za
            </Typography>
          </Box>

          <Box id="section-14">
            <Typography level="h2" sx={{ mb: 2 }}>
              14. Do We Make Updates to This Notice?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              <strong>
                <em>In Short:</em>
              </strong>{' '}
              <em>Yes, we will update this notice as necessary to stay compliant with relevant laws.</em>
            </Typography>
            <Typography level="body-md">
              We may update this Privacy Notice from time to time. The updated version will be indicated by an updated
              "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we
              may notify you either by prominently posting a notice of such changes or by directly sending you a
              notification. We encourage you to review this Privacy Notice frequently to be informed of how we are
              protecting your information.
            </Typography>
          </Box>

          <Box id="section-15">
            <Typography level="h2" sx={{ mb: 2 }}>
              15. How Can You Contact Us About This Notice?
            </Typography>
            <Typography level="body-md" sx={{ mb: 2 }}>
              If you have questions or comments about this notice, you may contact our Data Protection Officer (DPO) by
              email at federico@genieai.tech, by phone at +1 8573830065, or contact us by post at:
            </Typography>
            <Box sx={{ p: 3, bgcolor: 'background.level1', borderRadius: 'md' }}>
              <Typography level="body-md">
                <strong>Kindly Inc.</strong>
                <br />
                Data Protection Officer
                <br />
                82 Wendell Ave
                <br />
                Suite 100
                <br />
                Pittsfield, MA 01201
                <br />
                United States
              </Typography>
            </Box>
          </Box>

          <Box id="section-16">
            <Typography level="h2" sx={{ mb: 2 }}>
              16. How Can You Review, Update, or Delete the Data We Collect From You?
            </Typography>
            <Typography level="body-md">
              Based on the applicable laws of your country or state of residence in the US, you may have the right to
              request access to the personal information we collect from you, details about how we have processed it,
              correct inaccuracies, or delete your personal information. You may also have the right to withdraw your
              consent to our processing of your personal information. These rights may be limited in some circumstances
              by applicable law. To request to review, update, or delete your personal information, please fill out and
              submit a data subject access request.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
